"use client";
import * as React from "react";
import { useState } from "react";
import ConnectionList from "../ConnectionList/index";
import type {
  ConnectionData,
  ConnectionsWrapperProp,
  OIDCSSORecord,
  SAMLSSORecord,
} from "../types";
import defaultClasses from "./index.module.css";
import EditOIDCConnection from "../EditConnection/oidc/index";
import EditSAMLConnection from "../EditConnection/saml/index";
import Button from "../../../shared/Button/index";
import Spacer from "../../../shared/Spacer/index";
import Anchor from "../../../shared/Anchor/index";
import CreateSSOConnection from "../CreateConnection/index";
import { PaginatePayload } from "../../../shared/types";
const DEFAULT_VALUES = {
  connectionListData: [] as ConnectionData<any>[],
  view: "LIST" as "LIST" | "EDIT" | "CREATE",
};

function ConnectionsWrapper(props: ConnectionsWrapperProp) {
  const [ssoType, setSsoType] = useState(() => "saml");

  function handleNewConnectionTypeChange(event: any) {
    setSsoType(event.target.value);
  }

  const [connections, setConnections] = useState(
    () => DEFAULT_VALUES.connectionListData
  );

  function handleListFetchComplete(connectionsList: ConnectionData<any>[]) {
    setConnections(connectionsList);
  }

  const [view, setView] = useState(() => DEFAULT_VALUES.view);

  const [connectionToEdit, setConnectionToEdit] = useState(() => ({}));

  function connectionsAdded() {
    return connections.length > 0;
  }

  function ssoEnabled() {
    return (
      connectionsAdded() &&
      connections.some((connection) => connection.deactivated === false)
    );
  }

  function connectionFetchURL() {
    let _url = props.urls.get;
    const [urlPath, qs] = _url.split("?");
    const urlParams = new URLSearchParams(qs);
    urlParams.set("clientID", connectionToEdit.clientID);
    return `${urlPath}?${urlParams}`;
  }

  function switchToCreateView() {
    setView("CREATE");
  }

  function handleConnectionListActionClick(
    action: "edit" | "pageChange",
    payload: ConnectionData<any> | PaginatePayload
  ) {
    if (action === "edit") {
      setView("EDIT");
      setConnectionToEdit(payload);
    }
    if (action === "pageChange") {
      // call route change callback
    }
  }

  function switchToListView() {
    setView("LIST");
  }

  function createSuccessCallback(info: {
    operation: "CREATE";
    connection?: SAMLSSORecord | OIDCSSORecord;
    connectionIsSAML?: boolean;
    connectionIsOIDC?: boolean;
  }) {
    const { operation, connection, connectionIsSAML, connectionIsOIDC } = info;
    if (typeof props.successCallback === "function") {
      props.successCallback({
        operation,
        connection,
        connectionIsSAML,
        connectionIsOIDC,
      });
    }
    switchToListView();
  }

  function updateSuccessCallback(info: {
    connection: any;
    operation: "UPDATE" | "DELETE" | "COPY";
    connectionIsSAML?: boolean;
    connectionIsOIDC?: boolean;
  }) {
    const {
      connection,
      operation,
      connectionIsSAML = false,
      connectionIsOIDC = false,
    } = info;
    if (typeof props.successCallback === "function") {
      props.successCallback({
        operation,
        connection,
        connectionIsSAML,
        connectionIsOIDC,
      });
    }
    if (operation !== "COPY") {
      switchToListView();
    }
  }

  return (
    <div>
      {view === "LIST" ? (
        <div className={defaultClasses.listView}>
          <div className={defaultClasses.header}>
            <h5 className={defaultClasses.h5}>
              {props.title || "Manage SSO Connections"}
            </h5>
            <div className={defaultClasses.ctoa}>
              {props.urls?.spMetadata ? (
                <>
                  <Anchor
                    linkText="Access SP Metadata"
                    variant="button"
                    href={props.urls!.spMetadata!}
                  />
                  <Spacer x={4} />
                </>
              ) : null}
              <Button
                name="New Connection"
                handleClick={switchToCreateView}
                classNames={props.classNames?.button?.ctoa}
              />
            </div>
          </div>
          <Spacer y={8} />
          <ConnectionList
            {...props.componentProps.connectionList}
            urls={{
              get: props.urls.get,
            }}
            handleActionClick={handleConnectionListActionClick}
            handleListFetchComplete={handleListFetchComplete}
            tenant={props.defaults?.tenants || props.defaults?.tenant}
            product={props.defaults?.product}
          />
        </div>
      ) : null}
      {view === "EDIT" ? (
        <>
          <div className={defaultClasses.header}>
            <h5 className={defaultClasses.h5}>Edit SSO Connection</h5>
          </div>
          {connectionToEdit && "oidcProvider" in connectionToEdit ? (
            <EditOIDCConnection
              variant="basic"
              classNames={props.classNames}
              cancelCallback={switchToListView}
              errorCallback={props.errorCallback}
              successCallback={updateSuccessCallback}
              displayHeader={false}
              urls={{
                delete: props.urls.delete,
                patch: props.urls.patch,
                get: connectionFetchURL(),
              }}
              {...props.componentProps.editOIDCConnection}
            />
          ) : null}
          {connectionToEdit && "idpMetadata" in connectionToEdit ? (
            <EditSAMLConnection
              variant="basic"
              classNames={props.classNames}
              cancelCallback={switchToListView}
              errorCallback={props.errorCallback}
              successCallback={updateSuccessCallback}
              displayHeader={false}
              urls={{
                delete: props.urls.delete,
                patch: props.urls.patch,
                get: connectionFetchURL(),
              }}
              {...props.componentProps.editSAMLConnection}
            />
          ) : null}
        </>
      ) : null}
      {view === "CREATE" ? (
        <>
          <div className={defaultClasses.header}>
            <h5 className={defaultClasses.h5}>Create SSO Connection</h5>
          </div>
          <Spacer y={8} />
          <CreateSSOConnection
            classNames={props.classNames}
            cancelCallback={switchToListView}
            successCallback={createSuccessCallback}
            errorCallback={props.errorCallback}
            defaults={props.defaults}
            urls={{
              post: props.urls.post,
            }}
            {...props.componentProps.createSSOConnection}
          />
        </>
      ) : null}
    </div>
  );
}

export default ConnectionsWrapper;
